import React, { useState } from 'react';
import { deleteDocument, deleteAllDocuments, reindexDocuments } from '../../api/client';

const statusBadge = (status) => {
  const map = {
    indexed: { bg: 'rgba(72,187,120,0.15)', color: 'var(--success)', text: '✓ Indexed' },
    processing: { bg: 'rgba(246,173,85,0.15)', color: 'var(--warning)', text: '⏳ Processing' },
    error: { bg: 'rgba(252,129,129,0.15)', color: 'var(--error)', text: '✗ Error' },
  };
  const style = map[status] || map.processing;
  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      minWidth: 88,              // fits the longest label "✗ Error" → "⏳ Processing"
      maxWidth: '100%',          // never exceed the column
      height: 22,
      padding: '0 10px',
      borderRadius: 999,
      fontSize: 11.5,
      fontWeight: 600,
      lineHeight: 1,
      background: style.bg,
      color: style.color,
      whiteSpace: 'nowrap',
      boxSizing: 'border-box',
      verticalAlign: 'middle',
    }}>
      {style.text}
    </span>
  );
};

const fmtSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1048576).toFixed(1)} MB`;
};

const fmtDate = (dt) =>
  new Date(dt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const s = {
  card: {
    background: 'linear-gradient(135deg, #ffffff 0%, #f0fafa 60%, #e6f7f7 100%)',
    border: '1px solid rgba(29, 156, 156, 0.18)',
    borderRadius: '16px',
    padding: '32px',
    boxShadow: '0 4px 24px rgba(13, 79, 82, 0.06), 0 1px 2px rgba(0, 0, 0, 0.04)',
    position: 'relative',
    overflow: 'hidden',
  },
  cardAccent: {
    position: 'absolute',
    top: 0, left: 0, right: 0,
    height: '3px',
    background: 'linear-gradient(90deg, #0d4f52, #1d9c9c, #4fd1c5)',
  },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '22px' },
  title: { fontSize: '20px', fontWeight: 800, color: '#0f172a', letterSpacing: '-0.01em' },
  stats: { display: 'flex', gap: '14px', marginBottom: '22px' },
  stat: {
    padding: '18px 22px',
    background: 'rgba(255, 255, 255, 0.85)',
    border: '1px solid rgba(29, 156, 156, 0.15)',
    borderRadius: '12px',
    textAlign: 'center',
    flex: 1,
    boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
    backdropFilter: 'blur(4px)',
  },
  statNum: {
    fontSize: '30px',
    fontWeight: 800,
    background: 'linear-gradient(135deg, #0d4f52, #1d9c9c)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    lineHeight: 1,
  },
  statLabel: { fontSize: '11px', color: '#64748b', marginTop: '6px', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 },
  table: {
    width: '100%',
    borderCollapse: 'separate',
    borderSpacing: 0,
    background: '#ffffff',
    borderRadius: '14px',
    overflow: 'hidden',
    border: '1px solid rgba(29, 156, 156, 0.18)',
    boxShadow: '0 4px 14px rgba(13, 79, 82, 0.06)',
    tableLayout: 'fixed',          // honor column widths + enable ellipsis
  },
  tableWrap: {
    overflowX: 'auto',             // horizontal scroll if the table is wider than the card
    borderRadius: 14,
  },
  th: {
    padding: '14px 16px',
    textAlign: 'left',
    fontSize: '12px',
    fontWeight: 800,
    color: '#0d4f52',
    textTransform: 'uppercase',
    letterSpacing: '0.08em',
    borderBottom: '1px solid rgba(29, 156, 156, 0.22)',
    background: 'linear-gradient(135deg, rgba(29,156,156,0.10), rgba(13,115,119,0.05))',
  },
  td: {
    padding: '14px 16px',
    fontSize: '14px',
    borderBottom: '1px solid rgba(29, 156, 156, 0.08)',
    color: '#0f172a',
    verticalAlign: 'middle',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 240,              // forces ellipsis when content overflows
  },
  delBtn: {
    padding: '7px 18px',
    borderRadius: '999px',
    background: 'linear-gradient(135deg, #ffffff 0%, #fef2f2 100%)',
    border: '1px solid rgba(252, 129, 129, 0.4)',
    color: '#c53030',
    fontSize: '12.5px',
    cursor: 'pointer',
    transition: 'all 0.18s',
    fontWeight: 600,
    boxShadow: '0 2px 6px rgba(229, 62, 62, 0.12), 0 1px 2px rgba(0,0,0,0.04)',
    letterSpacing: '0.01em',
    lineHeight: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: 78,
    whiteSpace: 'nowrap',
  },
  reindexBtn: {
    padding: '9px 20px',
    borderRadius: '999px',
    background: 'linear-gradient(135deg, #1d9c9c 0%, #0d7377 100%)',
    border: 'none',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.18s',
    boxShadow: '0 4px 12px rgba(13, 115, 119, 0.25)',
    letterSpacing: '0.01em',
  },
  deleteAllBtn: {
    padding: '9px 20px',
    borderRadius: '999px',
    background: 'linear-gradient(135deg, #fc8181 0%, #e53e3e 100%)',
    border: 'none',
    color: '#ffffff',
    fontSize: '13px',
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'all 0.18s',
    boxShadow: '0 4px 12px rgba(229, 62, 62, 0.22)',
    letterSpacing: '0.01em',
  },
};

export default function DocumentList({ documents, onRefresh }) {
  const [reindexing, setReindexing] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deletingAll, setDeletingAll] = useState(false);
  const [productFilter, setProductFilter] = useState(null);   // null = All
  const availableProducts = React.useMemo(() => {
    const s = new Set();
    documents.forEach((d) => (d.products || []).forEach((p) => s.add(p)));
    return Array.from(s).sort();
  }, [documents]);
  const filteredDocs = React.useMemo(() => (
    productFilter ? documents.filter((d) => (d.products || []).includes(productFilter)) : documents
  ), [documents, productFilter]);

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.file_name}"? This cannot be undone.`)) return;
    setDeleting(doc.id);
    try {
      await deleteDocument(doc.id);
      onRefresh?.();
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete failed');
    }
    setDeleting(null);
  };

  const handleDeleteAll = async () => {
    if (!window.confirm(
      `Delete ALL ${documents.length} document(s)?\n\nThis will permanently remove every PDF file, clear the entire vector index, and wipe the query cache. This cannot be undone.`
    )) return;
    setDeletingAll(true);
    try {
      const res = await deleteAllDocuments();
      onRefresh?.();
      alert(`✅ ${res.data.message}\n${res.data.documents_deleted} document(s) deleted.`);
    } catch (err) {
      alert(err.response?.data?.detail || 'Delete all failed');
    }
    setDeletingAll(false);
  };

  const handleReindex = async () => {
    setReindexing(true);
    try {
      await reindexDocuments();
      onRefresh?.();
      alert('Reindex complete!');
    } catch (err) {
      alert(err.response?.data?.detail || 'Reindex failed');
    }
    setReindexing(false);
  };

  const totalChunks = filteredDocs.reduce((s, d) => s + (d.chunk_count || 0), 0);
  const totalPages = filteredDocs.reduce((s, d) => s + (d.page_count || 0), 0);

  return (
    <div style={s.card}>
      <div style={s.cardAccent} />
      <div style={s.header}>
        <h2 style={s.title}>Document Library</h2>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            style={s.reindexBtn}
            onClick={handleReindex}
            disabled={reindexing || documents.length === 0}
          >
            {reindexing ? '⏳ Reindexing…' : '🔄 Reindex All'}
          </button>
          <button
            style={{
              ...s.deleteAllBtn,
              opacity: (deletingAll || documents.length === 0) ? 0.5 : 1,
              cursor: (deletingAll || documents.length === 0) ? 'not-allowed' : 'pointer',
            }}
            onClick={handleDeleteAll}
            disabled={deletingAll || documents.length === 0}
            onMouseEnter={(e) => { if (documents.length > 0) { e.currentTarget.style.background = 'rgba(229,62,62,0.15)'; }}}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'rgba(229,62,62,0.08)'; }}
          >
            {deletingAll ? '⏳ Deleting…' : '🗑️ Delete All'}
          </button>
        </div>
      </div>

      <div style={s.stats}>
        <div style={s.stat}>
          <div style={s.statNum}>{filteredDocs.length}</div>
          <div style={s.statLabel}>Documents</div>
        </div>
        <div style={s.stat}>
          <div style={s.statNum}>{totalPages.toLocaleString()}</div>
          <div style={s.statLabel}>Total Pages</div>
        </div>
        <div style={s.stat}>
          <div style={s.statNum}>{totalChunks.toLocaleString()}</div>
          <div style={s.statLabel}>Index Chunks</div>
        </div>
      </div>

      {/* Product filter chips */}
      {availableProducts.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 22, alignItems: 'center' }}>
          <span style={{ fontSize: 11, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.08em', marginRight: 10, fontWeight: 700 }}>
            Filter by product:
          </span>
          <span
            onClick={() => setProductFilter(null)}
            style={{
              padding: '7px 16px',
              borderRadius: '999px',
              fontSize: '12.5px',
              cursor: 'pointer',
              transition: 'all 0.18s ease',
              userSelect: 'none',
              letterSpacing: '0.01em',
              background: productFilter === null
                ? 'linear-gradient(135deg, #1d9c9c 0%, #0d7377 100%)'
                : 'rgba(255, 255, 255, 0.85)',
              color: productFilter === null ? '#ffffff' : '#475569',
              border: '1px solid ' + (productFilter === null ? 'transparent' : 'rgba(29, 156, 156, 0.25)'),
              fontWeight: productFilter === null ? 700 : 500,
              boxShadow: productFilter === null
                ? '0 4px 12px rgba(13, 115, 119, 0.25), 0 1px 2px rgba(0,0,0,0.05)'
                : '0 1px 2px rgba(0,0,0,0.03)',
            }}
          >
            All ({documents.length})
          </span>
          {availableProducts.map((p) => {
            const count = documents.filter((d) => (d.products || []).includes(p)).length;
            const active = productFilter === p;
            return (
              <span
                key={p}
                onClick={() => setProductFilter(p)}
                style={{
                  padding: '7px 16px',
                  borderRadius: '999px',
                  fontSize: '12.5px',
                  cursor: 'pointer',
                  transition: 'all 0.18s ease',
                  userSelect: 'none',
                  letterSpacing: '0.01em',
                  background: active
                    ? 'linear-gradient(135deg, #1d9c9c 0%, #0d7377 100%)'
                    : 'rgba(255, 255, 255, 0.85)',
                  color: active ? '#ffffff' : '#475569',
                  border: '1px solid ' + (active ? 'transparent' : 'rgba(29, 156, 156, 0.25)'),
                  fontWeight: active ? 700 : 500,
                  boxShadow: active
                    ? '0 4px 12px rgba(13, 115, 119, 0.25), 0 1px 2px rgba(0,0,0,0.05)'
                    : '0 1px 2px rgba(0,0,0,0.03)',
                }}
              >
                {p} ({count})
              </span>
            );
          })}
        </div>
      )}

      {filteredDocs.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-muted)' }}>
          {documents.length === 0
            ? 'No documents uploaded yet. Upload PDFs above to get started.'
            : `No documents tagged "${productFilter}".`}
        </div>
      ) : (
        <div style={s.tableWrap}>
          <table style={s.table}>
            <colgroup>
              <col style={{ width: '24%' }} />{/* File Name */}
              <col style={{ width: '16%' }} />{/* Products  */}
              <col style={{ width: '70px' }} />{/* Pages   */}
              <col style={{ width: '90px' }} />{/* Chunks  */}
              <col style={{ width: '90px' }} />{/* Size    */}
              <col style={{ width: '140px' }} />{/* Uploaded By */}
              <col style={{ width: '120px' }} />{/* Uploaded     */}
              <col style={{ width: '130px' }} />{/* Status       */}
              <col style={{ width: '120px' }} />{/* Actions      */}
            </colgroup>
            <thead>
              <tr>
                <th style={s.th}>File Name</th>
                <th style={s.th}>Products</th>
                <th style={s.th}>Pages</th>
                <th style={s.th}>Chunks</th>
                <th style={s.th}>Size</th>
                <th style={s.th}>Uploaded By</th>
                <th style={s.th}>Uploaded</th>
                <th style={s.th}>Status</th>
                <th style={s.th}></th>
              </tr>
            </thead>
            <tbody>
              {filteredDocs.map((doc, idx) => (
                <tr
                  key={doc.id}
                  style={{
                    transition: 'background 0.15s',
                    background: idx % 2 === 0 ? '#ffffff' : 'rgba(29,156,156,0.035)',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(29,156,156,0.09)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = idx % 2 === 0 ? '#ffffff' : 'rgba(29,156,156,0.035)'; }}
                >
                  <td style={s.td} title={doc.file_name}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontWeight: 600, maxWidth: '100%' }}>
                      <span style={{ fontSize: 16, flexShrink: 0 }}>📄</span>
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {doc.file_name}
                      </span>
                    </span>
                  </td>
                  <td style={s.td} title={(doc.products || []).join(', ')}>
                    {(doc.products || []).length === 0
                      ? <span style={{ color: 'var(--text-muted)', fontSize: 11 }}>—</span>
                      : <div style={{ display: 'flex', flexWrap: 'nowrap', gap: 4, overflow: 'hidden' }}>
                          {doc.products.map((p) => (
                            <span key={p} style={{
                              padding: '2px 8px',
                              background: 'var(--accent-light)',
                              color: 'var(--accent)',
                              borderRadius: 10,
                              fontSize: 11,
                              fontWeight: 600,
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                            }}>{p}</span>
                          ))}
                        </div>
                    }
                  </td>
                  <td style={s.td}>{doc.page_count || '—'}</td>
                  <td style={s.td}>{doc.chunk_count?.toLocaleString() || '—'}</td>
                  <td style={s.td}>{fmtSize(doc.size_bytes)}</td>
                  <td style={s.td} title={doc.uploaded_by || ''}>
                    <span style={{
                      display: 'inline-block',
                      maxWidth: '100%',
                      padding: '2px 8px',
                      background: 'var(--bg-card)',
                      border: '1px solid var(--border)',
                      borderRadius: '4px',
                      fontSize: '12px',
                      color: 'var(--text-secondary)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      verticalAlign: 'middle',
                    }}>
                      👤 {doc.uploaded_by || '—'}
                    </span>
                  </td>
                  <td style={s.td}>{fmtDate(doc.upload_time)}</td>
                  <td style={s.td}>{statusBadge(doc.status)}</td>
                  <td style={{ ...s.td, textAlign: 'right', paddingRight: 18, paddingLeft: 12 }}>
                    <button
                      style={s.delBtn}
                      disabled={deleting === doc.id}
                      onClick={() => handleDelete(doc)}
                      onMouseEnter={(e) => { e.target.style.color = 'var(--error)'; e.target.style.borderColor = 'var(--error)'; }}
                      onMouseLeave={(e) => { e.target.style.color = 'var(--text-secondary)'; e.target.style.borderColor = 'var(--border-light)'; }}
                    >
                      {deleting === doc.id ? '…' : 'Delete'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
